import { Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class BuildVersionService implements OnDestroy {
  private readonly VERSION_STORAGE_KEY = 'app_build_version';
  private readonly POPUP_SHOWN_KEY = 'app_build_popup_shown';
  private currentBuildVersion: string | null = null;
  private isChecking = false;
  private visibilityHandler: (() => void) | null = null;
  private lastCheckTime = 0;
  private readonly CHECK_COOLDOWN = 30 * 1000; // 30 seconds cooldown

  constructor() {
    // Get current build version from meta tag (the version we're currently running)
    this.currentBuildVersion = this.getBuildVersionFromPage();
    if (this.currentBuildVersion) {
      const storedVersion = sessionStorage.getItem(this.VERSION_STORAGE_KEY);
      
      // If no stored version exists (first load) or stored version doesn't match current
      // Update stored version and clear popup flag
      if (!storedVersion || storedVersion !== this.currentBuildVersion) {
        sessionStorage.setItem(this.VERSION_STORAGE_KEY, this.currentBuildVersion);
        sessionStorage.removeItem(this.POPUP_SHOWN_KEY);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopChecking();
  }

  /**
   * Gets build version from meta tag in current page
   */
  private getBuildVersionFromPage(): string | null {
    try {
      const metaTag = document.querySelector('meta[name="build-version"]');
      return metaTag ? metaTag.getAttribute('content') : null;
    } catch (error) {
      console.warn('Error reading build version from page:', error);
      return null;
    }
  }

  /**
   * Re-initializes build version (useful after session expiration when storage is cleared)
   */
  reinitialize(): void {
    // Get current build version from meta tag
    const pageBuildVersion = this.getBuildVersionFromPage();
    if (pageBuildVersion) {
      const storedVersion = sessionStorage.getItem(this.VERSION_STORAGE_KEY);
      
      // If no stored version exists or it doesn't match current page version
      // Update stored version and clear popup flag
      if (!storedVersion || storedVersion !== pageBuildVersion) {
        this.currentBuildVersion = pageBuildVersion;
        sessionStorage.setItem(this.VERSION_STORAGE_KEY, pageBuildVersion);
        sessionStorage.removeItem(this.POPUP_SHOWN_KEY);
      } else {
        // Update currentBuildVersion to match stored
        this.currentBuildVersion = pageBuildVersion;
      }
    }
  }

  /**
   * Starts version checking - checks on visibility change and navigation
   */
  startChecking(): void {
    // Re-initialize build version in case session was cleared
    this.reinitialize();
    
    if (this.visibilityHandler) {
      return; // Already checking
    }

    // Check when tab becomes visible
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this.isChecking) {
        this.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Stops version checking
   */
  stopChecking(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.isChecking = false;
  }

  /**
   * Manually trigger a version check (called on route navigation)
   */
  checkOnNavigation(): void {
    const now = Date.now();
    if (now - this.lastCheckTime < this.CHECK_COOLDOWN) {
      return;
    }

    if (!this.isChecking && document.visibilityState === 'visible') {
      this.checkForUpdate();
    }
  }

  /**
   * Checks for updates by fetching index.html and comparing build versions
   */
  private async checkForUpdate(): Promise<void> {
    // Guard against concurrent checks
    if (this.isChecking) {
      return;
    }

    // Ensure we have current build version
    if (!this.currentBuildVersion) {
      this.currentBuildVersion = this.getBuildVersionFromPage();
      if (!this.currentBuildVersion) {
        return;
      }
    }

    // Ensure stored version exists (re-initialize if missing, e.g., after session expiration)
    let storedVersion = sessionStorage.getItem(this.VERSION_STORAGE_KEY);
    if (!storedVersion) {
      // Storage was cleared (e.g., session expiration), re-initialize
      this.reinitialize();
      storedVersion = sessionStorage.getItem(this.VERSION_STORAGE_KEY);
      if (!storedVersion) {
        return; // Still can't get version, skip check
      }
    }

    const popupShownForVersion = sessionStorage.getItem(this.POPUP_SHOWN_KEY);
    
    // Only check if we're running the same version as stored (not a fresh load)
    // If versions don't match, we just loaded a new build, don't check yet
    if (this.currentBuildVersion !== storedVersion) {
      return;
    }

    // Don't check if we've already shown popup for the stored version
    if (popupShownForVersion === storedVersion) {
      return; // Already shown popup for this version
    }

    // Set checking flag before async operation
    this.isChecking = true;
    this.lastCheckTime = Date.now();

    try {
      const timestamp = Date.now();
      const response = await fetch(`/index.html?t=${timestamp}`, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        return;
      }

      const html = await response.text();
      const serverBuildVersion = this.extractBuildVersionFromHtml(html);

      // Only show popup if server has a DIFFERENT version than what we're running
      if (serverBuildVersion && serverBuildVersion !== this.currentBuildVersion) {
        // Check if we've already shown popup for this server version
        const alreadyShown = sessionStorage.getItem(this.POPUP_SHOWN_KEY);
        if (alreadyShown !== serverBuildVersion) {
          // Mark that we've shown popup for this server version
          sessionStorage.setItem(this.POPUP_SHOWN_KEY, serverBuildVersion);
          
          // Dispatch event to show popup (non-blocking)
          window.dispatchEvent(new CustomEvent('app-build-update'));
        }
      }
    } catch (error) {
      // Silently fail - network errors are expected
    } finally {
      // Always reset checking flag
      this.isChecking = false;
    }
  }

  /**
   * Extracts build version from HTML string
   */
  private extractBuildVersionFromHtml(html: string): string | null {
    try {
      const match = html.match(/<meta\s+name=["']build-version["']\s+content=["']([^"']+)["']/i);
      return match ? match[1] : null;
    } catch (error) {
      console.warn('Error extracting build version from HTML:', error);
      return null;
    }
  }
}
